<template>
    <div class="progress-content" :class="{ 'is-overview-compact': compact }">
        <!-- 구분 -->
        <div class="progress-header">
            <span class="division-name">{{ item.divCdName }}</span>
        </div>

        <!-- 진행 막대 -->
        <div class="progress-bars">
            <div class="progress-bar-container">
                <b-progress
                    ref="progress"
                    :value="
                        getProgressValue(
                            getOriginalValue(item.currentConsumpt, item.originalCurrentConsumpt),
                            globalMaxValue
                        )
                    "
                    :max="100"
                    class="custom-progress boxshadow"
                    :class="{ 'bar-no-label': compact }"
                    :style="{
                        '--progress-color': itemColors[item.divCd] || '#6c757d',
                        height: computedBarHeight,
                    }"
                />

                <!-- 전일 마커 -->
                <div
                    v-if="showPrevData.prevDay"
                    class="progress-marker previous-marker"
                    :style="{
                        left:
                            getProgressValue(
                                getOriginalValue(item.prevConsumpt, item.originalPrevConsumpt),
                                globalMaxValue
                            ) + '%',
                        height: computedBarHeight,
                    }"
                    :title="`전일: ${formatValue(item.prevConsumpt)}`"
                />

                <!-- 전년 마커 -->
                <div
                    v-if="showPrevData.prevYear"
                    class="progress-marker last-year-marker"
                    :style="{
                        left:
                            getProgressValue(
                                getOriginalValue(item.lastConsumpt, item.originalLastConsumpt),
                                globalMaxValue
                            ) + '%',
                        height: computedBarHeight,
                    }"
                    :title="`전년: ${formatValue(item.lastConsumpt)}`"
                />
            </div>
        </div>

        <!-- compact: 막대 밖 수치 (겹침 방지) -->
        <div v-if="compact" class="progress-value-aside" :title="displayLabel">
            {{ displayLabel }}
        </div>
    </div>
</template>

<script>
export default {
    name: "ProgressBar",
    props: {
        item: { type: Object, required: true },
        itemColors: { type: Object, required: true },
        showPrevData: { type: Object, required: true },
        unit: { type: String, required: true },
        cardHeight: { type: Number, required: true },
        usageData: { type: Array, required: true },
        labelOptions: { type: Object, required: false },
        compact: { type: Boolean, default: false },
    },
    data() {
        return {
            toFixedConstant: this.labelOptions?.toFixedConstant || 0,
        };
    },
    computed: {
        /** 모든 current/prev/last 중 최댓값 (원본 값 기준) */
        globalMaxValue() {
            const values = this.usageData.flatMap((i) => {
                const current = i.originalCurrentConsumpt != null ? i.originalCurrentConsumpt : i.currentConsumpt;
                const prev = i.originalPrevConsumpt != null ? i.originalPrevConsumpt : i.prevConsumpt;
                const last = i.originalLastConsumpt != null ? i.originalLastConsumpt : i.lastConsumpt;
                return [current, prev, last];
            });
            return Math.max(...values);
        },
        computedBarHeight() {
            if (this.compact) {
                if (this.cardHeight <= 5) return "14px";
                if (this.cardHeight <= 10) return "18px";
                return "22px";
            }
            if (this.cardHeight <= 5) return "25px";
            if (this.cardHeight <= 10) return "35px";
            return "45px";
        },
        displayLabel() {
            const displayUnit = this.item.displayUnit || this.unit;
            return `${this.formatValue(this.item.currentConsumpt)} ${displayUnit}`;
        },
    },
    mounted() {
        this.injectLabel();
    },
    watch: {
        item: {
            handler() {
                this.$nextTick(this.injectLabel);
            },
            deep: true,
        },
        compact() {
            this.$nextTick(this.injectLabel);
        },
    },
    methods: {
        getProgressValue(val, max) {
            return max === 0 ? 0 : Math.min(Math.round((val / max) * 100), 100);
        },
        formatValue(v) {
            if (v == null || isNaN(v)) return "0";
            if (this.toFixedConstant > 0) {
                return Number(v).toFixed(this.toFixedConstant);
            }
            return v.toLocaleString();
        },
        getOriginalValue(convertedValue, originalValue) {
            return originalValue != null ? originalValue : convertedValue;
        },
        /** PC: 막대 안 라벨 / compact: 막대 안 텍스트 비움 */
        injectLabel() {
            this.$nextTick(() => {
                const bar = this.$refs.progress?.$el?.querySelector(".progress-bar");
                if (!bar) return;
                if (this.compact) {
                    bar.textContent = "";
                    return;
                }
                const displayUnit = this.item.displayUnit || this.unit;
                bar.textContent = `${this.formatValue(this.item.currentConsumpt)} ${displayUnit}`;
            });
        },
    },
};
</script>

<style>
.progress-content {
    display: flex;
    align-items: center;
}

.progress-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 50px;
    flex-shrink: 0;
}

.division-name {
    font-weight: bold;
    font-size: 12px;
    color: #333;
    text-align: center;
    word-break: keep-all;
}

.current-value {
    font-size: 15px;
    font-weight: bold;
    color: #91949a;
}

.progress-bars {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 15px;
    min-width: 0;
}

.progress-bar-container {
    display: flex;
    flex-direction: column;
    position: relative;
}

.custom-progress {
    border-radius: 0 20px 20px 0;
}

.custom-progress .progress-bar {
    border-radius: 0 20px 20px 0;
    font-size: 12px;
    transition: width 0.3s ease;
    background-color: var(--progress-color, #6c757d) !important;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    padding-left: 10px;
    overflow: hidden;
    white-space: nowrap;
    box-shadow:
        rgba(0, 0, 0, 0.17) 0px -23px 25px 0px inset,
        rgba(0, 0, 0, 0.15) 0px -36px 30px 0px inset,
        rgba(0, 0, 0, 0.1) 0px -79px 40px 0px inset,
        rgba(0, 0, 0, 0.06) 0px 2px 1px,
        rgba(0, 0, 0, 0.09) 0px 4px 2px,
        rgba(0, 0, 0, 0.09) 0px 8px 4px,
        rgba(0, 0, 0, 0.09) 0px 16px 8px,
        rgba(0, 0, 0, 0.09) 0px 32px 16px;
}

.custom-progress.bar-no-label .progress-bar {
    padding-left: 0;
    font-size: 0;
    color: transparent;
}

.custom-progress:hover .progress-bar {
    opacity: 0.8;
}

.progress-marker {
    position: absolute;
    width: 2px;
    cursor: pointer;
    transition: all 0.3s ease;
    z-index: 10;
    font-size: 18px;
    background-image: repeating-linear-gradient(
        to bottom,
        transparent,
        transparent 2px,
        currentColor 2px,
        currentColor 4px
    );
    background-size: 2px 6px;
}

.progress-marker:hover {
    transform: scaleX(1.5);
    box-shadow: 0 0 8px rgba(0, 0, 0, 0.3);
}

.previous-marker {
    background-color: #f55667;
}

.last-year-marker {
    background-color: #3dc1fe;
}

.unit {
    font-size: 8px;
}

.boxshadow {
    box-shadow:
        rgba(14, 63, 126, 0.04) 0px 0px 0px 1px,
        rgba(42, 51, 69, 0.04) 0px 1px 1px -0.5px,
        rgba(42, 51, 70, 0.04) 0px 3px 3px -1.5px,
        rgba(42, 51, 70, 0.04) 0px 6px 6px -3px,
        rgba(14, 63, 126, 0.04) 0px 12px 12px -6px,
        rgba(14, 63, 126, 0.04) 0px 24px 24px -12px;
}

.progress-value-aside {
    flex-shrink: 0;
    width: 72px;
    margin-left: 6px;
    font-size: 10px;
    font-weight: 600;
    color: #495057;
    text-align: right;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.progress-content.is-overview-compact .progress-header {
    width: 36px;
}

.progress-content.is-overview-compact .division-name {
    font-size: 10px;
    line-height: 1.15;
}

.progress-content.is-overview-compact .progress-bars {
    gap: 4px;
}
</style>
